import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Stethoscope } from "lucide-react";

/** READ-ONLY record-integrity checks: records that LOOK complete and aren't.
 *  Writes nothing, repairs nothing — the owner rules after seeing the counts.
 *  The inventory-health KPIs will read this SAME endpoint (NG-5: one implementation). */
export function RecordIntegrityChecks() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const run = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/reports/record-integrity");
      setData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { run(); }, [run]);

  const clean = data && data.checks.every((c) => c.count === 0);

  return (
    <section data-testid="record-integrity" className="mt-10">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2">
          <Stethoscope className="w-5 h-5 text-[#B01E28]" />Record integrity
        </h2>
        <Button data-testid="integrity-refresh" variant="outline" size="sm" disabled={busy}
          onClick={run} className="rounded-lg border-slate-300 shrink-0">
          {busy ? "Checking…" : "Re-check"}
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Records that look complete and aren’t. Read-only — nothing here is repaired automatically.
      </p>

      {!data ? (
        <div className="p-8 text-center font-mono text-xs text-slate-400">CHECKING…</div>
      ) : (
        <>
          {clean && (
            <div data-testid="integrity-all-clear"
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-3 text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              All four checks came back empty across {data.scope["live parts scanned"]} live parts and{" "}
              {data.scope["live trucks scanned"]} live trucks.
            </div>
          )}
          <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 bg-white overflow-hidden">
            {data.checks.map((c) => {
              const isOpen = open === c.key;
              const bad = c.count > 0;
              return (
                <div key={c.key} data-testid={`integrity-check-${c.key}`}>
                  <button type="button" data-testid={`integrity-toggle-${c.key}`}
                    onClick={() => setOpen(isOpen ? null : c.key)}
                    className="w-full flex items-start gap-3 p-3 text-left hover:bg-slate-50 transition-colors">
                    {bad ? <AlertTriangle className="w-5 h-5 text-[#C76714] mt-0.5 shrink-0" />
                      : <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium flex items-center gap-2">
                        <span data-testid={`integrity-count-${c.key}`}
                          className={`font-mono font-bold ${bad ? "text-[#B01E28]" : "text-slate-400"}`}>{c.count}</span>
                        {c.title}
                      </div>
                      <div className="text-[12px] text-slate-500 mt-0.5">{c.meaning}</div>
                    </div>
                    {bad && (isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 mt-1" />
                      : <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />)}
                  </button>
                  {isOpen && bad && (
                    <div data-testid={`integrity-rows-${c.key}`} className="bg-slate-50/60 px-3 pb-3">
                      <div className="text-[11px] text-slate-500 mb-2">
                        <span className="font-bold uppercase tracking-wider">Repair path — </span>{c.repair}
                      </div>
                      <div className="space-y-1">
                        {c.rows.slice(0, 50).map((r, i) => (
                          <div key={i} data-testid={`integrity-row-${c.key}-${i}`}
                            className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] bg-white border border-slate-200 rounded-lg px-2 py-1.5">
                            {r.part_id && (
                              <Link data-testid={`integrity-link-part-${r.code}`} to={`/parts/${r.part_id}`}
                                className="font-mono font-bold text-[#B01E28] hover:underline">{r.code}</Link>
                            )}
                            {r.donor_id && (
                              <Link data-testid={`integrity-link-truck-${r.truck}`} to={`/donors/${r.donor_id}`}
                                className="font-mono font-bold text-[#B01E28] hover:underline">{r.truck}</Link>
                            )}
                            {r.truck_id && !r.donor_id && (
                              <Link to={`/donors/${r.truck_id}`} className="font-mono font-bold text-[#B01E28] hover:underline">
                                open truck
                              </Link>
                            )}
                            <span className="text-slate-600">
                              {[r.name, r.component, r.example,
                                r.options_unfindable ? `${r.options_unfindable} option(s) unfindable` : null,
                                r.extras_unfindable ? `${r.extras_unfindable} extra(s) unfindable` : null,
                                r.count ? `${r.count} extra(s) stranded` : null,
                                r.first ? `first ${r.first.part} → second ${r.second?.part}` : null,
                              ].filter(Boolean).join(" · ")}
                            </span>
                          </div>
                        ))}
                        {c.rows.length > 50 && (
                          <div className="text-[11px] text-slate-500 pt-1">
                            showing the first 50 of {c.count}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div data-testid="integrity-scope" className="font-mono text-[11px] text-slate-400 mt-2">
            scope: {data.scope["live parts scanned"]} live parts · {data.scope["live trucks scanned"]} live trucks ·
            up to {data.scope["row cap per check"]} rows per check · run at {(data.scope.generated_at || "").slice(0, 16).replace("T", " ")}
            {data.synthetic?.active && (
              <span data-testid="integrity-scope-synthetic" className="block mt-1 text-[#B01E28] font-bold">
                SYNTHETIC trucks included in this scan: {data.synthetic.synthetic} · real trucks: {data.synthetic.real}
                {" "}— these counts are not real fleet numbers.
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
